import React, { useState, useEffect } from 'react';
import { Award, Download, Share2, Calendar, CheckCircle, Lock } from 'lucide-react';
import { learningAPI } from '../../services/api';

const Certificates = () => {
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchCertificates();
    }, []);

    const fetchCertificates = async () => {
        try {
            const response = await learningAPI.getCertificates();
            setCertificates(response.data.certificates || []);
        } catch (err) {
            console.error('Error fetching certificates:', err);
            setError('Unable to load certificates');
            // Use empty array instead of mock data
            setCertificates([]);
        } finally {
            setLoading(false);
        }
    };

    const getPlaceholderCertificates = () => [
            {
                id: 1,
                course_name: 'English Fluency Mastery',
                completion_date: '2026-02-10',
                issued_date: '2026-02-12',
                certificate_id: 'CERT-2026-001234',
                level: 'Advanced',
                score: 92,
                status: 'issued',
                badge_color: '#27B05B'
            },
            {
                id: 2,
                course_name: 'Grammar Excellence',
                completion_date: '2026-01-20',
                issued_date: '2026-01-22',
                certificate_id: 'CERT-2026-001122',
                level: 'Intermediate',
                score: 85,
                status: 'issued',
                badge_color: '#3498DB'
            },
            {
                id: 3,
                course_name: 'Business Communication',
                completion_date: null,
                issued_date: null,
                certificate_id: null,
                level: 'Advanced',
                score: 78,
                status: 'in_progress',
                progress: 78,
                badge_color: '#FF9800'
            }
        ];

    const handleDownload = (certId) => {
        alert(`Downloading certificate ${certId}...`);
        // Implement actual download logic
    };

    const handleShare = (cert) => {
        if (navigator.share) {
            navigator.share({
                title: `${cert.course_name} Certificate`,
                text: `I've earned a certificate in ${cert.course_name}!`,
                url: window.location.href
            });
        } else {
            alert('Share functionality not supported on this browser');
        }
    };

    const handleVerify = (certId) => {
        alert(`Verify certificate at: https://verify.drsabitha.com/${certId}`);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Loading your certificates...</p>
                </div>
            </div>
        );
    }

    const earnedCertificates = certificates.filter(c => c.status === 'issued');
    const inProgressCertificates = certificates.filter(c => c.status === 'in_progress');

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>My Certificates</h1>
                    <p style={styles.subtitle}>Your achievements and credentials</p>
                </div>
                <div style={styles.statsCard}>
                    <Award size={32} color="#FFD700" />
                    <div>
                        <div style={styles.statValue}>{earnedCertificates.length}</div>
                        <div style={styles.statLabel}>Earned Certificates</div>
                    </div>
                </div>
            </div>

            {earnedCertificates.length > 0 && (
                <>
                    <h2 style={styles.sectionTitle}>Earned Certificates</h2>
                    <div style={styles.certificatesGrid}>
                        {earnedCertificates.map(cert => (
                            <div key={cert.id} style={styles.certificateCard}>
                                <div style={styles.certificateHeader}>
                                    <div style={{...styles.certificateBadge, backgroundColor: cert.badge_color}}>
                                        <Award size={40} color="white" />
                                    </div>
                                    <div style={styles.verifiedBadge}>
                                        <CheckCircle size={16} color="#27B05B" />
                                        Verified
                                    </div>
                                </div>

                                <div style={styles.certificateBody}>
                                    <h3 style={styles.courseName}>{cert.course_name}</h3>
                                    
                                    <div style={styles.certificateDetails}>
                                        <div style={styles.detailRow}>
                                            <span style={styles.detailLabel}>Certificate ID:</span>
                                            <span style={styles.detailValue}>{cert.certificate_id}</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span style={styles.detailLabel}>Level:</span>
                                            <span style={{...styles.levelBadge, backgroundColor: cert.badge_color}}>
                                                {cert.level}
                                            </span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span style={styles.detailLabel}>Score:</span>
                                            <span style={styles.scoreBadge}>{cert.score}%</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <Calendar size={16} color="#666" />
                                            <span style={styles.dateText}>
                                                Issued: {new Date(cert.issued_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={styles.certificateActions}>
                                        <button onClick={() => handleDownload(cert.certificate_id)} style={styles.actionButton}>
                                            <Download size={18} />
                                            Download
                                        </button>
                                        <button onClick={() => handleShare(cert)} style={styles.actionButton}>
                                            <Share2 size={18} />
                                            Share
                                        </button>
                                    </div>

                                    <button 
                                        onClick={() => handleVerify(cert.certificate_id)}
                                        style={styles.verifyButton}
                                    >
                                        Verify Certificate
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {inProgressCertificates.length > 0 && (
                <>
                    <h2 style={{...styles.sectionTitle, marginTop: '50px'}}>In Progress</h2>
                    <div style={styles.certificatesGrid}>
                        {inProgressCertificates.map(cert => (
                            <div key={cert.id} style={{...styles.certificateCard, ...styles.certificateCardLocked}}>
                                <div style={styles.certificateHeader}>
                                    <div style={{...styles.certificateBadge, backgroundColor: '#95A5A6'}}>
                                        <Lock size={40} color="white" />
                                    </div>
                                </div>

                                <div style={styles.certificateBody}>
                                    <h3 style={styles.courseName}>{cert.course_name}</h3>
                                    
                                    <div style={styles.progressSection}>
                                        <div style={styles.progressHeader}>
                                            <span>Course Progress</span>
                                            <span style={styles.progressPercentage}>{cert.progress}%</span>
                                        </div>
                                        <div style={styles.progressBarContainer}>
                                            <div 
                                                style={{
                                                    ...styles.progressBar,
                                                    width: `${cert.progress}%`,
                                                    backgroundColor: cert.badge_color
                                                }}
                                            />
                                        </div>
                                        <p style={styles.progressHint}>
                                            Complete the course with 80%+ score to earn your certificate
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {certificates.length === 0 && (
                <div style={styles.empty}>
                    <Award size={64} color="#ccc" />
                    <h2 style={styles.emptyTitle}>No certificates yet</h2>
                    <p style={styles.emptyText}>
                        Complete courses to earn certificates and showcase your achievements
                    </p>
                </div>
            )}

            <div style={styles.infoBox}>
                <h3 style={styles.infoTitle}>About Certificates</h3>
                <ul style={styles.infoList}>
                    <li>All certificates are digitally verified and can be shared on LinkedIn</li>
                    <li>Certificates are awarded upon course completion with 80%+ score</li>
                    <li>Each certificate has a unique ID for verification</li>
                    <li>Download certificates in PDF format anytime</li>
                </ul>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px',
        flexWrap: 'wrap',
        gap: '20px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    subtitle: {
        fontSize: '16px',
        color: '#666',
        marginTop: '8px'
    },
    statsCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        padding: '20px 30px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    statLabel: {
        fontSize: '14px',
        color: '#666'
    },
    sectionTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '20px'
    },
    certificatesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
        gap: '30px',
        marginBottom: '40px'
    },
    certificateCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease'
    },
    certificateCardLocked: {
        opacity: 0.7
    },
    certificateHeader: {
        position: 'relative',
        height: '150px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    certificateBadge: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '5px solid white',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
    },
    verifiedBadge: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: 'white',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        color: '#27B05B'
    },
    certificateBody: {
        padding: '25px'
    },
    courseName: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '20px',
        textAlign: 'center'
    },
    certificateDetails: {
        marginBottom: '20px'
    },
    detailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid #F0F0F0'
    },
    detailLabel: {
        fontSize: '14px',
        color: '#666',
        fontWeight: '500'
    },
    detailValue: {
        fontSize: '14px',
        color: '#2C3E50',
        fontWeight: '600',
        fontFamily: 'monospace'
    },
    levelBadge: {
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        color: 'white'
    },
    scoreBadge: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#27B05B'
    },
    dateText: {
        fontSize: '14px',
        color: '#666'
    },
    certificateActions: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        marginBottom: '15px'
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        backgroundColor: '#F5F5F5',
        color: '#2C3E50',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    verifyButton: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#3498DB',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    progressSection: {
        padding: '20px',
        backgroundColor: '#F9F9F9',
        borderRadius: '8px'
    },
    progressHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#2C3E50'
    },
    progressPercentage: {
        color: '#27B05B'
    },
    progressBarContainer: {
        width: '100%',
        height: '10px',
        backgroundColor: '#E0E0E0',
        borderRadius: '5px',
        overflow: 'hidden',
        marginBottom: '12px'
    },
    progressBar: {
        height: '100%',
        borderRadius: '5px',
        transition: 'width 0.5s ease'
    },
    progressHint: {
        fontSize: '13px',
        color: '#666',
        fontStyle: 'italic'
    },
    infoBox: {
        padding: '30px',
        backgroundColor: '#F0F8FF',
        borderRadius: '12px',
        border: '2px solid #3498DB',
        marginTop: '40px'
    },
    infoTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '15px'
    },
    infoList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    empty: {
        textAlign: 'center',
        padding: '80px 20px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    emptyTitle: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#2C3E50',
        marginTop: '20px',
        marginBottom: '10px'
    },
    emptyText: {
        fontSize: '16px',
        color: '#666'
    }
};

export default Certificates;
